import { apiProblemText } from "../../../api/api-error.js";
import { HelpPlace } from "../../../help/place/use-help-place.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent header"` plus `data-screen-label="Agent"` — the header, the
 * nine-tab strip, and whichever tab body the URL asks for.
 *
 * The tab is a search parameter rather than component state, so a tab is a link: the
 * conversation someone is looking at in History can be sent to a colleague and will open on
 * the same conversation. The same is true of the range, the outcome filter and the search
 * text — all five live in the URL and all five are typed.
 *
 * AMENDED 2026-09-25 (customer-portal review #105, #106, #110, #120, #33): the header's state and
 * actions come from `./lifecycle-panel.tsx` — a tag that says "Live, not reachable" when nothing
 * reaches a live agent, Pause / Resume / Remove agent that work, and a panel that leads a failed
 * agent's page with the reason and Try again / Replace document. An agent with no live version
 * does not offer the tabs that need one (Live, Orders, Outbound, Test) nor the test panel, and the
 * Orders tab carries no count: the order book is the workspace's, so a number on one agent's tab
 * counted other agents' orders.
 *
 * AMENDED 2026-10-09 (design sync): the strip draws the design's seven groups (`tabGroupsOf`), and a
 * group of two tabs (Conversations: History · Review; Insights: Analytics · Results; Settings:
 * Channels · Versions) draws them as an underlined second row. The header no longer carries "Test
 * agent": the Test tab IS the test panel now (`./test-call-tab.tsx`), and on every other tab the
 * docked panel or its rail is there to open, as the design draws it.
 *
 * AMENDED 2026-10-09 (plan-015 AGC-UI, gap G3, owner answer 2): an agent whose live version takes payment ONLY by a
 * checkout link, while the organization cannot offer online payment now, takes a message instead of an order. The page
 * says so above every tab, from `getVoiceAgent`'s `online_payment_unavailable`, until online payment is on again or
 * the agent accepts another way to pay.
 *
 * AMENDED 2026-10-28 (plan-029 WAPP-ACCESS; design §40.25.2, D-12 as changed at AP-0): an agent under the internal access
 * lock says so above every tab but Channels — "will soon answer only the people on its list" or "refuses every caller
 * until you add the people who may call it" — with "Choose who may call", which opens Channels, where the access panel
 * draws the same warning beside the list (`./channels/access-lock.tsx`).
 *
 * AMENDED 2026-10-28 (plan-030 WAPP-Q, design §41.10): a survey agent's Questions tab is the survey question editor
 * (`./questions/questions-tab.tsx`). Which version it shows (the one the editor edits, or another one read-only, opened
 * from Versions) is page state rather than a search parameter: Versions' own `agentVersion` stays Versions'.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Icon, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { AnalyticsTab } from "./analytics-tab.js";
import { AgentVersionsTab } from "./versions-tab.js";
import { GapAndConversationFlagTab, type ConcernPane } from "./review-tab.js";
import {
  VOICE_AGENT_TAB,
  isSurveyAgentTemplate,
  isVoiceAgentTabId,
  tabGroupsOf,
  tabsFor,
  type VoiceAgentTabGroupId,
  type VoiceAgentTabId,
} from "./agent-tabs.js";
import { ChannelBindingTab } from "./channels/channel-binding-tab.js";
import { PhoneConnectBanner, PhoneConnectDialog } from "./channels/phone-connect.js";
import { PartyAccessLockWarning } from "./channels/access-lock.js";
import { DeferredTab } from "./deferred-tab.js";
import { HistoryTab } from "./history-tab.js";
import { OrderBookTab } from "./orders/orders-tab.js";
import { VoiceAgentHeadRow, voiceAgentStatusOf } from "./lifecycle-panel.js";
import type { CustomerOrderFilter } from "./orders/order-book-gateway.js";
import { LiveTab } from "./live-tab.js";
import { OutboundTab } from "./outbound-tab.js";
import { ResultsTab } from "./results-tab.js";
import { QuestionsTab } from "./questions/questions-tab.js";
import { TestCallTab } from "./test-call-tab.js";
import { useSandboxPanelOpen } from "./sandbox-panel-preference.js";
import { SandboxRail, TestChatPanel } from "../../test-console/test-chat-panel.js";
import { GLYPH } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";
import {
  conversationFlagQuery,
  gapQuery,
  liveSnapshotQuery,
  voiceAgentQuery,
} from "../../../api/query.js";
import type {
  AnalyticsRange,
  HistoryChannelLabelFilter,
  HistoryOutcomeFilter,
  HistoryRange,
  SurveyResultRangeView,
  SurveyVariantView,
} from "../../../api/gateway.js";
import type { SurveyResultViewMode } from "../../campaigns/survey-result-body.js";
import type { RespondentFilterValue } from "../../campaigns/survey-result-copy.js";

import "./agent-detail.css";
import "../new/answering.css";
export interface VoiceAgentScreenSearch {
  readonly tab: VoiceAgentTabId;
  readonly range: HistoryRange;
  readonly outcome: HistoryOutcomeFilter;
  readonly q: string;
  readonly conversation: string;
  /**
   * History's channel chip and one party's conversations (plan-004 WC-90). Optional so the links
   * that open History from elsewhere need not name them; absent reads as "all" and "".
   */
  readonly channelLabel?: HistoryChannelLabelFilter;
  readonly party?: string;
  /**
   * numbers-sip-sync: one number's calls — a `phone_number_id`, from Numbers & SIP's "See all calls on this number".
   * Optional for the reason `party` is; absent reads as "".
   */
  readonly number?: string;
  /**
   * Analytics' own window, Versions' selected version and Review's pane.
   *
   * Separate from History's `range` on purpose: the two tabs answer different questions over
   * different windows, and one shared parameter would mean opening Analytics silently
   * re-filtered History. All three are in the URL for the same reason the first five are —
   * "v6, and why it was restored" is a link somebody sends a colleague.
   */
  readonly analyticsRange: AnalyticsRange;
  readonly agentVersion: string;
  readonly concern: ConcernPane;
  /**
   * The Orders tab's status chip.
   *
   * Its own parameter rather than History's `outcome`, for the reason `analyticsRange` is its
   * own rather than History's `range`: the two tabs answer different questions, and one
   * shared parameter would mean opening Orders silently re-filtered History. The search box
   * DOES share `q`, because "find PZ-4A12" and "find that caller" are the same act on both.
   */
  readonly orderStatus: CustomerOrderFilter;
  /**
   * The Results tab's filters (survey VoiceAgents, 2026-10-03). Optional, as `channelLabel` is, so the
   * links that open other tabs need not name them; absent reads as every conversation, real ones only,
   * By question. Their own parameters for the reason `analyticsRange` is its own.
   */
  readonly resultRange?: SurveyResultRangeView;
  readonly resultIncludeIsTest?: boolean;
  readonly resultCampaign?: string;
  readonly resultLanguage?: string;
  readonly resultVariant?: SurveyVariantView | "";
  readonly resultView?: SurveyResultViewMode;
  readonly resultOutcome?: RespondentFilterValue;
}

export interface VoiceAgentScreenProps {
  readonly voiceAgentId: string;
  readonly search: VoiceAgentScreenSearch;
  readonly onSearch: (next: Partial<VoiceAgentScreenSearch>) => void;
}

/**
 * The tabs that show what a LIVE version does (#110): with no live version there is nothing
 * happening (the Live tab's read answers 404), no order it could have taken, nothing to call out
 * with and nothing to test, so they are left out rather than loading forever or saying nothing.
 */
const NEEDS_LIVE_VERSION: ReadonlySet<VoiceAgentTabId> = new Set(["live", "orders", "outbound", "test"]);

export function VoiceAgentScreen({ voiceAgentId, search, onSearch }: VoiceAgentScreenProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const voiceAgent = useQuery(voiceAgentQuery(gateway, voiceAgentId));
  const hasLiveVersion = voiceAgent.data !== undefined && voiceAgent.data.liveAgentVersionId !== null;
  // The strip's count badges read the same queries the tabs do, so a badge and the tab it
  // sits on can never disagree. The live snapshot only exists for a live version (#110).
  const liveSnapshot = useQuery({ ...liveSnapshotQuery(gateway, voiceAgentId), enabled: hasLiveVersion });
  const gap = useQuery(gapQuery(gateway, voiceAgentId));
  const conversationFlag = useQuery(conversationFlagQuery(gateway, voiceAgentId));
  // Review #96: the test panel starts closed — it took a third of the width on every tab — and
  // the page remembers, per person, whether they last left it open. Closed leaves the rail.
  const [chatPreference, setChatOpen] = useSandboxPanelOpen();
  // #33: no test panel until there is a version to test.
  const chatOpen = hasLiveVersion && chatPreference;
  // The connect dialog (design sync 2026-10-09): opened by the banner and by the Phone card.
  const [connecting, setConnecting] = useState(false);
  // plan-030 WAPP-Q: the version the Questions tab shows read-only; `null` for the one the editor edits.
  const [questionVersion, setQuestionVersion] = useState<string | null>(null);

  if (voiceAgent.data === undefined) {
    // 2026-10-14 (live bug): a failed read said "Opening the agent…" for ever. It says it failed, with Try again.
    if (voiceAgent.isError) {
      return (
        <section className="c-page" role="alert">
          <p className="c-note c-note-critical">
            {portalText("operations", "the_agent_could_not_be_opened")} {apiProblemText(voiceAgent.error, "")}
          </p>
          <Button
            variant="primary"
            disabled={voiceAgent.isFetching}
            onClick={() => {
              void voiceAgent.refetch();
            }}
          >
            {portalText("operations", "try_again_042c862e")}
          </Button>
        </section>
      );
    }
    return <p className="c-note">{portalText("operations", "opening_the_agent_82f35488")}</p>;
  }

  const one = voiceAgent.data;
  const tabs = tabsFor(one.agentTemplateCode).filter((tab) => hasLiveVersion || !NEEDS_LIVE_VERSION.has(tab.id));
  const requestedTab = VOICE_AGENT_TAB.find((tab) => tab.id === search.tab);
  // A link to a tab this agent does not offer (a failed agent's Live tab) opens Versions, where
  // its builds are, rather than a tab that has nothing to show.
  const currentTab =
    requestedTab !== undefined && tabs.includes(requestedTab)
      ? requestedTab
      : (tabs.find((tab) => tab.id === "versions") ?? tabs[0]);
  const status = voiceAgentStatusOf(one);
  const survey = isSurveyAgentTemplate(one.agentTemplateCode);

  const openConcernCount =
    gap.data === undefined || conversationFlag.data === undefined
      ? null
      : gap.data.filter((row) => row.state === "open").length +
        conversationFlag.data.filter((row) => !row.handled).length;

  const badgeFor = (id: VoiceAgentTabId): string | null => {
    switch (id) {
      case "live":
        return liveSnapshot.data === undefined ? null : String(liveSnapshot.data.liveConversationCount);
      case "orders":
        // No count: the order book is the workspace's, and a number here counted orders other
        // agents took (#110). The tab itself lists the book.
        return null;
      case "review":
        return openConcernCount === null ? null : String(openConcernCount);
      case "versions":
        return one.liveAgentVersionId === null ? null : one.liveAgentVersionLabel;
      default:
        return null;
    }
  };

  const groups = tabGroupsOf(tabs);
  const currentGroup = groups.find((group) => group.tab.some((tab) => tab.id === currentTab?.id)) ?? groups[0];
  /** A group's badge is the badge of the tab inside it that has one (Review's count, Versions' label). */
  const groupBadgeFor = (id: VoiceAgentTabGroupId): string | null => {
    const group = groups.find((each) => each.id === id);
    if (group === undefined) return null;
    for (const tab of group.tab) {
      const badge = badgeFor(tab.id);
      if (badge !== null) return badge;
    }
    return null;
  };
  // The Test tab is the test panel itself, so the docked one steps aside there.
  const dockShown = hasLiveVersion && currentTab?.id !== "test";

  return (
    <div className="c-va" data-chat={!dockShown ? "none" : chatOpen ? "open" : "closed"}>
      {/* The help assistant knows the tab shown and the tabs offered; its "Go to {tab}" card opens a tab here. */}
      <HelpPlace
        place={currentTab?.id ?? "versions"}
        placeList={tabs.map((tab) => tab.id)}
        move={(tab) => {
          if (isVoiceAgentTabId(tab)) onSearch({ tab });
        }}
      />
      <div className="c-va-head" data-screen-label="Agent header">
        <Link to="/agents" search={{ status: "all", q: "" }} className="c-va-back">
          <Icon glyph={GLYPH.arrowLeft} size="sm" />
          {portalText("operations", "all_agents_b94d6ccc")}</Link>

        <VoiceAgentHeadRow
          voiceAgent={one}
          heading={
            <div>
              <p className="c-va-kicker">
                {one.agentTemplateName} · {one.partyNumber ?? portalText("operations", "no_number_connected_yet_fdf8636f")}
              </p>
              <h1 className="c-va-title">
                {one.displayName}
                <Tag status={status.tone}>{status.label}</Tag>
              </h1>
            </div>
          }
          extraAction={null}
          onOpenTab={(tab) => {
            onSearch({ tab });
          }}
        />

        {/* Review #39, #116: the strip WRAPS rather than scrolling behind a "More tabs" arrow, so
            every tab and its whole label is always on screen, the chosen one included. On a phone
            the strip is replaced by one menu of the same sections (review #118). */}
        <label className="c-va-strip-menu">
          <span className="c-va-strip-menu-label">{portalText("operations", "section_f2c6b564")}</span>
          <select
            className="input"
            value={currentTab?.id ?? ""}
            onChange={(event) => {
              const chosen = tabs.find((tab) => tab.id === event.currentTarget.value);
              if (chosen !== undefined) onSearch({ tab: chosen.id });
            }}
          >
            {tabs.map((tab) => {
              const badge = tab.delivered ? badgeFor(tab.id) : "soon";
              return (
                <option key={tab.id} value={tab.id}>
                  {badge === null ? tab.label : `${tab.label} · ${badge}`}
                </option>
              );
            })}
          </select>
        </label>
        <div className="c-va-strip-wrap">
          <div className="c-va-strip" role="tablist" aria-label={portalText("operations", "agent_sections_37742c28")}>
            {groups.map((group) => {
              const badge = groupBadgeFor(group.id);
              const current = group.id === currentGroup?.id;
              const first = group.tab[0];
              return (
                <button
                  type="button"
                  role="tab"
                  key={group.id}
                  title={group.label}
                  className="c-va-tab"
                  aria-current={current ? "page" : undefined}
                  aria-selected={current}
                  onClick={() => {
                    // Already in the group: stay on the tab its second row shows.
                    if (!current && first !== undefined) onSearch({ tab: first.id });
                  }}
                >
                  <span className="c-va-tab-icon">
                    <Icon glyph={group.glyph} size="lg" />
                  </span>
                  <span>{group.label}</span>
                  {badge === null ? null : <span className="c-va-tab-badge">{badge}</span>}
                </button>
              );
            })}
          </div>
        </div>
        {currentGroup === undefined || currentGroup.tab.length < 2 ? null : (
          <div className="c-va-subtabs" role="tablist" aria-label={currentGroup.label}>
            {currentGroup.tab.map((tab) => {
              const badge = tab.id === "review" || tab.id === "versions" ? badgeFor(tab.id) : null;
              const selected = tab.id === currentTab?.id;
              return (
                <button
                  type="button"
                  role="tab"
                  key={tab.id}
                  className="c-va-subtab"
                  aria-selected={selected}
                  onClick={() => {
                    onSearch({ tab: tab.id });
                  }}
                >
                  {tab.label}
                  {badge === null ? null : <span className="c-va-subtab-badge">{badge}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <section className="c-va-body" data-screen-label="Agent">
        {hasLiveVersion ? (
          <PhoneConnectBanner
            voiceAgentId={voiceAgentId}
            outbound={survey}
            onConnect={() => {
              setConnecting(true);
            }}
          />
        ) : null}
        {connecting ? (
          <PhoneConnectDialog
            voiceAgentId={voiceAgentId}
            outbound={survey}
            onClose={() => {
              setConnecting(false);
            }}
          />
        ) : null}
        {one.detail?.onlinePaymentUnavailable === true ? <OnlinePaymentUnavailableWarning /> : null}
        {currentTab?.id === "channels" ? null : (
          <PartyAccessLockWarning
            voiceAgentId={voiceAgentId}
            onOpenAccess={() => {
              onSearch({ tab: "channels" });
            }}
          />
        )}
        {currentTab === undefined ? null : currentTab.id === "live" ? (
          <LiveTab
            voiceAgentId={voiceAgentId}
            selectedConversationId={search.conversation}
            onSelect={(conversationId) => {
              onSearch({ conversation: conversationId });
            }}
            survey={survey}
          />
        ) : currentTab.id === "history" ? (
          <HistoryTab
            voiceAgentId={voiceAgentId}
            range={search.range}
            outcome={search.outcome}
            search={search.q}
            channelLabel={search.channelLabel ?? "all"}
            partyOf={search.party ?? ""}
            phoneNumberId={search.number ?? ""}
            onNumber={(number) => {
              onSearch({ number });
            }}
            selectedConversationId={search.conversation}
            onRange={(range) => {
              onSearch({ range });
            }}
            onChannelLabel={(channelLabel) => {
              onSearch({ channelLabel });
            }}
            onParty={(party) => {
              // A visitor's earlier conversations are older than any window: "All" shows every one.
              onSearch(party === "" ? { party } : { party, range: "all", conversation: party });
            }}
            onOutcome={(outcome) => {
              onSearch({ outcome });
            }}
            onSearch={(q) => {
              onSearch({ q });
            }}
            onSelect={(conversationId) => {
              onSearch({ conversation: conversationId });
            }}
            surveyAnswer={survey}
          />
        ) : currentTab.id === "results" ? (
          <ResultsTab
            voiceAgentId={voiceAgentId}
            filter={{
              range: search.resultRange ?? "all",
              includeIsTest: search.resultIncludeIsTest ?? false,
              campaignId: search.resultCampaign === undefined || search.resultCampaign === "" ? null : search.resultCampaign,
              languageCode: search.resultLanguage === undefined || search.resultLanguage === "" ? null : search.resultLanguage,
              variant: search.resultVariant === undefined || search.resultVariant === "" ? null : search.resultVariant,
            }}
            view={search.resultView ?? "question"}
            outcome={search.resultOutcome ?? "all"}
            onSearch={(next) => {
              const filter = next.filter;
              onSearch({
                ...(filter === undefined
                  ? {}
                  : {
                      resultRange: filter.range,
                      resultIncludeIsTest: filter.includeIsTest,
                      resultCampaign: filter.campaignId ?? "",
                      resultLanguage: filter.languageCode ?? "",
                      resultVariant: filter.variant ?? "",
                    }),
                ...(next.view === undefined ? {} : { resultView: next.view }),
                ...(next.outcome === undefined ? {} : { resultOutcome: next.outcome }),
              });
            }}
          />
        ) : currentTab.id === "analytics" ? (
          <AnalyticsTab
            voiceAgentId={voiceAgentId}
            range={search.analyticsRange}
            onRange={(analyticsRange) => {
              onSearch({ analyticsRange });
            }}
            onOpenGaps={() => {
              onSearch({ tab: "review", concern: "gaps" });
            }}
          />
        ) : currentTab.id === "questions" ? (
          <QuestionsTab
            voiceAgentId={voiceAgentId}
            agentVersionId={questionVersion}
            onShowAgentVersion={setQuestionVersion}
            onOpenAgentVersion={(agentVersion) => {
              onSearch(agentVersion === null ? { tab: "versions" } : { tab: "versions", agentVersion });
            }}
          />
        ) : currentTab.id === "versions" ? (
          <AgentVersionsTab
            voiceAgentId={voiceAgentId}
            selectedAgentVersionId={search.agentVersion}
            onSelect={(agentVersion) => {
              onSearch({ agentVersion });
            }}
            onOpenChannelBindingTab={() => {
              onSearch({ tab: "channels" });
            }}
            onOpenQuestions={
              survey
                ? (agentVersionId) => {
                    setQuestionVersion(agentVersionId);
                    onSearch({ tab: "questions" });
                  }
                : null
            }
          />
        ) : currentTab.id === "review" ? (
          <GapAndConversationFlagTab
            voiceAgentId={voiceAgentId}
            liveVersionSeq={one.liveAgentVersionId === null ? null : one.liveVersionSeq}
            pane={search.concern}
            onPane={(concern) => {
              onSearch({ concern });
            }}
            onOpenConversation={(conversationId) => {
              // "All": the call a gap or a flag points at may be older than any shorter window,
              // and History must open on it rather than on an empty filter (review #93).
              onSearch({ tab: "history", conversation: conversationId, range: "all", outcome: "all", q: "" });
            }}
            onOpenAgentVersion={(agentVersion) => {
              // G9: saving answers makes a draft; the prototype jumps to it on Versions.
              onSearch({ tab: "versions", agentVersion });
            }}
          />
        ) : currentTab.id === "orders" ? (
          // P12-03. The order book: the figures, the filter, the table, and the three
          // actions — open the shop screen on a counter tablet, print, export.
          <OrderBookTab
            voiceAgentId={voiceAgentId}
            filter={search.orderStatus}
            search={search.q}
            onFilter={(orderStatus) => {
              onSearch({ orderStatus });
            }}
            onSearch={(q) => {
              onSearch({ q });
            }}
            onConnectPos={() => {
              onSearch({ tab: "versions" });
            }}
          />
        ) : currentTab.id === "channels" ? (
          // P11-04. Concurrency, the bindings, the connection with rotate, the signalling
          // trace, the "when it cannot connect" states and when it answers.
          <ChannelBindingTab
            voiceAgentId={voiceAgentId}
            outbound={survey}
            onTestCall={() => {
              onSearch({ tab: "test" });
            }}
            onConnect={() => {
              setConnecting(true);
            }}
            onSeeCallers={() => {
              onSearch({ tab: "history", range: "7d", outcome: "all", q: "" });
            }}
            onOpenAgentVersion={(agentVersion) => {
              onSearch({ tab: "versions", agentVersion });
            }}
          />
        ) : currentTab.id === "outbound" ? (
          <OutboundTab voiceAgentId={voiceAgentId} />
        ) : currentTab.id === "test" ? (
          <TestCallTab voiceAgent={one} />
        ) : (
          <DeferredTab tab={currentTab} />
        )}
      </section>

      {dockShown ? (
        <div className="c-va-dock">
          {chatOpen ? (
            <TestChatPanel
              voiceAgentId={voiceAgentId}
              voiceAgentName={one.displayName}
              languageCode={one.languageCode}
              onClose={() => {
                setChatOpen(false);
              }}
              // Review #117/#169: this agent's phone test is its own Test tab, never a console that
              // forgets which agent it was. plan-004 WC-51 (FR-BV-1 (a)): passing it also offers the
              // panel's Voice mode, which runs the browser test right here in the panel.
              onOpenTestCall={() => {
                onSearch({ tab: "test" });
              }}
            />
          ) : (
            <SandboxRail
              hasHistory={false}
              onOpen={() => {
                setChatOpen(true);
              }}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

/** G3: the live version takes only a checkout link, and the organization cannot offer online payment now. */
function OnlinePaymentUnavailableWarning(): ReactNode {
  usePortalTranslation("operations");
  return (
    <p className="c-answer-warn" role="status">
      <Icon glyph={GLYPH.warning} size="lg" />
      <span>
        <strong>{portalText("operations", "callers_cannot_order_right_now_62a31c9b")}</strong> {portalText("operations", "this_agent_takes_payment_only_by_a_checkout_link_and_onlin_4ffd3c89")}<Link to="/billing">{portalText("operations", "billing_abaec452")}</Link>{portalText("operations", "or_let_callers_pay_on_delivery_8a8a6378")}</span>
    </p>
  );
}
